import { ChangeDetectorRef, Component } from '@angular/core';
import { CheckoutAccountComponentDef } from '@df/checkout/checkout-account.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { CheckoutFormComponent } from './../form/checkout-form.component';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { GtmService } from '@df/module-gtm/gtm.service';
import { NeedLoginService } from '@df/session/need-login.service';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { GTM_FORM_FIELD_ERROR_MESSAGE, LocalGtmService } from '../../module/gtm/gtm.service.decorator';
import { MuiInputDirective } from '../../module/legacy/forms/mui-input.directive';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { Section1Component } from '../../ui/display/section1/section1.component';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { RadioComponent } from '../../ui/form/radio/radio.component';
import { ViewPasswordComponent } from '../../ui/form/view-password.component';
import { CheckoutChangeSectionComponent } from '../change-section/checkout-change-section.component';
import { CheckoutExpandableComponent } from '../expandable/checkout-expandable.component';
import { CheckoutStepComponent } from './../step/checkout-step.component';
enum EAccountStatus {
    welcome,
    loggedIn,
    login,
    registration,
    newUser,
    guestCheckout
}
@Component({
    selector: 'checkout-account',
    templateUrl: './checkout-account.html',
    standalone: true,
    imports: [
        FormsModule,
        CheckoutExpandableComponent,
        InputWrapComponent,
        Section1Component,
        CommonModule,
        ResultComponent,
        TranslationModule,
        ActionComponent,
        CheckoutChangeSectionComponent,
        MuiInputDirective,
        ViewPasswordComponent,
        RadioComponent,
        SizeClassDirective,
        InputWrapBodyDirective,
        RouterLink
    ]
})
export class CheckoutAccountComponent extends CheckoutAccountComponentDef {
    private formErrorSend = false;

    get expandableId(): string[] {
        return this.checkoutFormComponent.getExpandableId('account');
    }
    get expandableOff(): boolean {
        return this.checkoutFormComponent.isExpandableOff('account');
    }
    /**
     * Show only email input
     */
    get currentStepName(): string {
        return this.checkoutFormComponent.currentStep.name;
    }
    /**
     * Shipping address data
     */
    get shippingData(): Checkout.IAddress {
        return this.checkoutFormComponent.data.address.shipping;
    }
    /**
     * Status types
     */
    readonly EAccountStatus = EAccountStatus;
    /**
     * Class constructor
     */
    constructor(
        public override checkoutFormComponent: CheckoutFormComponent,
        public override checkoutStepComponent: CheckoutStepComponent,
        requestService: RequestService,
        customer: Customer,
        app: App,
        ref: ChangeDetectorRef,
        needLoginService: NeedLoginService,
        timeoutService: TimeoutService,
        private readonly gtmService: GtmService
    ) {
        super(checkoutFormComponent, checkoutStepComponent, requestService, customer, app, ref, needLoginService, timeoutService);
    }
    get sectionTitle(): string {
        if (this.checkoutStepComponent.isCurrent) {
            switch (this.accountStatus) {
                case EAccountStatus.loggedIn:
                    return `Welcome back ${this.customer.model.data.firstname} ${this.customer.model.data.lastname}`;
                case EAccountStatus.login:
                    return 'Welcome back!';
                case EAccountStatus.newUser:
                    return 'Welcome';
            }
        }
        return 'Your email';
    }
    /**
     * Retrieves type of display for the form
     */
    get accountStatus(): EAccountStatus {
        if (this.isLoggedIn) {
            return EAccountStatus.loggedIn;
        }
        if (this.isGuestCheckout) {
            return EAccountStatus.guestCheckout;
        }
        if (this.shouldLogin()) {
            return EAccountStatus.login;
        }
        if (this.shouldRegister()) {
            return EAccountStatus.registration;
        }
        if (this.isNewUser()) {
            return EAccountStatus.newUser;
        }
        return EAccountStatus.welcome;
    }
    /**
     * Continue button action
     */
    continue(): void | Promise<any> {
        // do not do anything while emails is being checked
        if (this.checkStatus.busy) {
            return;
        }
        this.sendGA4Error();

        switch (this.accountStatus) {
            case EAccountStatus.loggedIn:
            case EAccountStatus.guestCheckout:
            case EAccountStatus.newUser:
                return this.goToNextStep();
            case EAccountStatus.login:
                return this.login(() => {
                    if (this.isLoggedIn) {
                        this.goToNextStep();
                    }
                });
            case EAccountStatus.registration:
                return this.register();
            case EAccountStatus.welcome:
                return this.check();
        }
    }
    /**
     * Continue button action
     */
    get continueStatus(): Status | undefined {
        switch (this.accountStatus) {
            case EAccountStatus.login:
                return this.loginStatus;
            case EAccountStatus.registration:
                return this.registerStatus;
            case EAccountStatus.welcome:
                return this.checkStatus;
        }
        return undefined;
    }
    /**
     * Continue button action
     */
    get continueLabel(): string {
        switch (this.accountStatus) {
            case EAccountStatus.login:
                return 'Login';
            case EAccountStatus.loggedIn:
                return 'Continue to delivery'
            case EAccountStatus.newUser:
                return 'Continue as Guest';
            case EAccountStatus.guestCheckout:
                return this.isChecked() ? 'Continue as Guest' : 'Continue';
            default:
                return 'Continue';
        }
    }
    /**
     * Switch to guest mode
     */
    continueAsGuest(): void {
        if (!this.checkStatus.busy && this.shouldLogin()) {
            this.setGuestCheckout();
            this.timeoutService.setTimeout(() => {
                this.goToNextStep();
            }, 50);
        }
    }
    /**
     * Advance to the next step (after account)
     */
    protected goToNextStep(): void {
        if (this.isValid) {
            this.form && (this.form.submitAttempt = false);
            this.checkoutFormComponent.goToNextStep('account');
        } else {
            this.form && (this.form.submitAttempt = true);
            this.form?.ngSubmit.emit();
            this.sendGA4Error();
        }
    }
    /**
     * Adding form validation to super method
     * Should be in core, but keeing here to avoid legacy problems
     */
    protected override getIsValid(): boolean {
        return !!super.getIsValid() && !!this.form?.valid;
    }

    private sendGA4Error = (e?: { error: string }): void => {
        if (!this.formErrorSend && !this.form?.valid) {
            this.formErrorSend = true;
            const step = this.checkoutStepComponent;
            (this.gtmService as LocalGtmService).sendFormError(e?.error || GTM_FORM_FIELD_ERROR_MESSAGE, 'checkout ' + step.name, 1);
        }
    };
}
